import { FunctionComponent, useCallback, useEffect, useState } from "react";
import Icon from "@mdi/react";
import { debounce } from "lodash";
import { mdiAlertCircleOutline, mdiArrowTopRight, mdiRefresh } from "@mdi/js";
import moment from "moment";
import { ClosingStockModel } from "../models/closing_stock_model";
import { WarehouseModel } from "../models/warehouse_model";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import { fetchGet, toQueryString } from "../../../service/api_client";
import { UTCToLocalDate } from "../../../utils/date_util";
import LoadingWidget from "../../../context_providers/modal/loader";
import Pagination from "../../../ui/new_components/common/pagination";
import { ProductItemCreationSubCategoryElectricRawMaterials } from "../items/enums/enums";
import ItemSearchField from "../items/components/ItemSearchField";
import WarehouseMultiSelect from "../warehouse/warehouse_multi_select";
import { formatAmountWithCommas } from "../../../ui/pages/content_panel/utils/helpers";
import { GetWareHouseListApi } from "../inventory_basket/components/warehouse_repo";

interface ClosingStockListScreenProps {
  records: ClosingStockModel[];
  onClick: (id: string) => void;
  sort_by: string;
  sort_order: number;
  pages: number;
  options: []; // Replace with actual type
  handleSort: (data: { sort: string; sort_order: number }) => void;
  handlePageChange: (page: number) => void;
}

interface StockListFilter {
  warehouse_id?: string;
  warehouses?: string[];
  warehouse_type?: string;
  warehouse_sub_type?: string;
  item_code?: string;
  item_name: string;
  item_type?: string;
  category?: string;
  sub_category?: string;
  installation_type?: string;
  as_of_date?: Date;
  search?: string;
  page: number;
  count: number;
  all?: any;
}

const defaultFilter = (): StockListFilter => {
  return {
    page: 1,
    count: 20,
    item_name: "",
  };
};

const ClosingStockListScreen: FunctionComponent<
  ClosingStockListScreenProps
> = () => {
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/npd/closing_stock`;
  const { showToast } = useToast();
  const tempFilter = localStorage.getItem("cs_filter");
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [data, setData] = useState<ClosingStockModel[]>([]);
  const [filter, setFilter] = useState<StockListFilter>(
    tempFilter ? JSON.parse(tempFilter) : defaultFilter(),
  );
  const [selectedWarehouses, setSelectedWarehouses] = useState<
    WarehouseModel[]
  >([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHandler = useCallback(
    debounce((prop: StockListFilter) => {
      if (prop) fetchData(prop);
    }, 300),
    [],
  );

  useEffect(() => {
    localStorage.setItem("cs_filter", JSON.stringify(filter));
  }, [filter]);

  useEffect(() => {
    debouncedHandler(filter);
  }, [filter, debouncedHandler]);

  useEffect(() => {
    const loadSelectedWarehouses = async () => {
      if (filter.warehouses && filter.warehouses.length > 0) {
        const res = await GetWareHouseListApi({
          page: 1,
          count: 50,
          all: true,
        });
        if (res.success) {
          const allWarehouses = res.data.data;
          const hydrated = filter.warehouses
            .map((id) => allWarehouses.find((w: WarehouseModel) => w.id === id))
            .filter(Boolean) as WarehouseModel[];
          setSelectedWarehouses(hydrated);
        }
      }
    };

    loadSelectedWarehouses();
  }, []);

  const fetchData = async (prop: StockListFilter) => {
    setLoading(true);
    try {
      const res = await fetchGet(url + toQueryString(prop));
      if (res.success) {
        setData(res.data.data);
        if (res.data.metadata) {
          setTotal(res.data.metadata.total);
        }
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error: any) {
      showToast({ type: ToastType.error, text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const getClosingStockExport = async () => {
    try {
      setLoading(true);

      const searchProp = { ...filter };
      searchProp.all = true;
      const response = await ExportClosingStackIntentory(searchProp);

      if (response.success) {
        showToast({
          type: ToastType.success,
          text: response.message,
        });
      } else {
        showToast({
          type: ToastType.error,
          text: response.message,
        });
      }
    } catch (error: any) {
      console.error("Export initiation error:", error);

      showToast({ type: ToastType.error, text: error.error });
    } finally {
      setLoading(false);
    }
  };
  const ExportClosingStackIntentory = async (props: StockListFilter) => {
    const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/npd/closing-stock-export`;

    const res = await fetchGet(url + toQueryString(props));
    return res;
  };

  const clearLocalStore = () => {
    // Remove from localStorage
    localStorage.removeItem("cs_filter");

    // Reset filter state
    const defaultFilterObj = defaultFilter();
    setFilter(defaultFilterObj);

    // Trigger UI updates and fetch
    debouncedHandler(defaultFilterObj);
    fetchData(defaultFilterObj);
  };

  return (
    <>
      <div
        id="page_container"
        className="flex flex-col justify-between h-full p-1"
      >
        <section
          id="top_section"
          className="flex justify-between items-end my-1"
        >
          <h1 className="font-bold text-2xl text-gray-500 flex items-center gap-3">
            Closing Stock
            <button
              id="refresh_btn"
              title="Refersh"
              onClick={() => debouncedHandler({ ...filter })}
            >
              <Icon path={mdiRefresh} className="h-6 w-6"></Icon>
            </button>
            <button
              id="clear_filter_btn"
              className="flex gap-1 items-center cursor-pointer rounded p-1 bg-myPrimaryColor hover:scale-105 duration-200 text-sm text-white transform"
              onClick={() => clearLocalStore()}
            >
              Clear Filter
            </button>
          </h1>

          <div className="text-sm flex flex-wrap items-center gap-2 m-3">
            <div className="flex flex-wrap text-sm items-center gap-2 ">
              <button
                id="export_btn"
                onClick={() => getClosingStockExport()}
                className="rounded-lg border px-2 py-1 hover:bg-gray-800 hover:text-white flex gap-2 items-center"
              >
                <Icon path={mdiArrowTopRight} size="15"></Icon> Export
              </button>
            </div>
            {/* <div className="flex gap-2">
              <h6 className="font-normal">Filters</h6>
              <div
                onClick={() => setFilterCollapsed(!filterCollaped)}
                className="flex   items-center justify-center bg-gray-200 p-1 rounded-full hover:bg-gray-800 hover:text-white"
              >
                <Icon
                  path={filterCollaped ? mdiMenuDown : mdiMenuUp}
                  className="w-4 h-4"
                />
              </div>
            </div> */}
          </div>
        </section>
        <section className="flex items-start justify-start m-4">
          <div className="grid grid-cols-1 md:grid-cols-2  lg:grid-cols-2 xl:grid-cols-5 gap-6 w-full">
            <div className="">
              <label
                htmlFor="app_status"
                className="text-md pt-1 font-semibold"
              >
                Item Code
              </label>
              <ItemSearchField
                defaulValue={filter.item_code ?? ""}
                onClear={() => {
                  setFilter((prevData) => ({
                    ...prevData,
                    item_code: "", // Directly updating item_code
                  }));
                }}
                onSubmit={(value: string) =>
                  setFilter((prevData) => ({
                    ...prevData,
                    item_code: value, // Directly updating item_code
                  }))
                }
              />
            </div>
            <div className="">
              <label
                htmlFor="app_status"
                className="text-md pt-1 font-semibold"
              >
                Item Name
              </label>
              <ItemSearchField
                defaulValue={filter.item_name ?? ""}
                onClear={() => {
                  setFilter((prevData) => ({
                    ...prevData,
                    item_name: "", // Directly updating item name
                  }));
                }}
                onSubmit={(val) => {
                  setFilter((prevData) => ({
                    ...prevData,
                    item_name: val, // Directly updating item name
                  }));
                }}
              />
            </div>
            {/* <div className="">
                <label
                  htmlFor="item_type"
                  className="text-md pt-1 font-semibold"
                >
                  Item Type
                </label>
                <select
                  className="w-full p-2 bg-white rounded focus:outline-none"
                  name="item_type"
                  id="item_type"
                  value={selectedType ?? ""}
                  onChange={(e) => {
                    setFilter((prevData) => ({
                      ...prevData,
                      sub_category: "",
                      category: "",
                      item_type: e.target.value,
                    }));
                    setSelectedType(e.target.value);
                    setSelectedItems([]);
                  }}
                >
                  <option value="">select</option>
                  {ItemTypeList.map((f: string) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </div> */}
            {/* {selectedType === "Product" && (
                <div>
                  <label
                    htmlFor="category"
                    className="text-md pt-1 font-semibold"
                  >
                    Category
                  </label>
                  <select
                    className="w-full p-2 rounded bg-white focus:outline-none"
                    name="category"
                    id="category"
                    value={filter.category ?? ""}
                    onChange={(e) => {
                      setFilter((prevData) => ({
                        ...prevData,
                        category: e.target.value, // Directly updating category
                      }));
                    }}
                  >
                    <option value="">select</option>
                    {Object.entries([
                      "Electrical",
                      "Electronics",
                      "Others",
                    ]).map(([key, value]) => (
                      <option key={key} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
              )} */}
            {/* {filter.item_type && ( */}
            <div>
              <label
                htmlFor="sub_category"
                className="text-md pt-1 font-semibold"
              >
                Type
              </label>
              <select
                className="w-full p-2 bg-white rounded focus:outline-none"
                name="sub_category"
                id="sub_category"
                value={filter.sub_category ?? ""}
                onChange={(e) => {
                  // Handle the change event
                  setFilter((prevData) => ({
                    ...prevData,
                    sub_category: e.target.value, // Update the sub_category in state
                  }));
                }}
              >
                <option value="">select</option>
                {/* Render different sub-category options based on filter.type and filter.category */}
                {/* {filter.item_type === "Product" ? (
                  ProductItemCreationSubCategoryElectricRawMaterials.map(
                    (subCategory, index) => (
                      <option key={index} value={subCategory}>
                        {subCategory}
                      </option>
                    ),
                  )
                ) : filter.item_type === "" ? (
                  <></>
                ) : (
                  ServiceItemCreationSubCategory.map((subCategory, index) => (
                    <option key={index} value={subCategory}>
                      {subCategory}
                    </option>
                  ))
                )} */}
                {ProductItemCreationSubCategoryElectricRawMaterials.map(
                  (subCategory, index) => (
                    <option key={index} value={subCategory}>
                      {subCategory}
                    </option>
                  ),
                )}
              </select>
            </div>
            {/* )} */}

            {/* <div>
                <label
                  htmlFor="installation_type"
                  className="text-md pt-1 font-semibold"
                >
                  Installation Type
                </label>
                <select
                  className="w-full p-2 bg-white rounded focus:outline-none"
                  name="installation_type"
                  id="installation_type"
                  value={filter.installation_type ?? ""}
                  onChange={(e) => {
                    setFilter((prevData) => ({
                      ...prevData,
                      installation_type: e.target.value,
                    }));
                  }}
                >
                  <option value="">select</option>
                  <option value="Installation Raw Materials">
                    Installation Raw Materials
                  </option>
                  <option value="Installation Consumables">
                    Installation Consumables
                  </option>
                </select>
              </div> */}
            <div className="flex flex-col gap-1">
              <label className="text-md pt-1 font-semibold">Warehouse</label>

              <WarehouseMultiSelect
                onSelect={(selectedWarehouse: WarehouseModel) => {
                  setFilter((prevFilter) => {
                    // Prevent duplicate selections
                    if (
                      prevFilter.warehouses?.some(
                        (w) => w === selectedWarehouse.id,
                      )
                    ) {
                      return prevFilter; // Ignore duplicate entry
                    }

                    return {
                      ...prevFilter,
                      warehouses: [
                        ...(prevFilter?.warehouses || []),
                        selectedWarehouse.id,
                      ],
                    };
                  });

                  setSelectedWarehouses((prevWarehouses) => {
                    // Prevent duplicate selections
                    if (
                      prevWarehouses.some((v) => v.id === selectedWarehouse.id)
                    ) {
                      return prevWarehouses; // Ignore duplicate entry
                    }

                    return [...prevWarehouses, selectedWarehouse]; // Append the new vendor
                  });
                }}
                onDelete={(id: string) => {
                  setFilter((prevFilter) => ({
                    ...prevFilter,
                    warehouses: (prevFilter?.warehouses || []).filter(
                      (w) => w !== id,
                    ),
                  }));

                  setSelectedWarehouses((prevWarehouses) =>
                    prevWarehouses.filter((v) => v.id !== id),
                  );
                }}
                selectedWarehouses={selectedWarehouses ?? []}
              />
              {/* <WarehouseSearchField
                  value={filter?.warehouse}
                  // onSelect={() => {
                  //   setFilter({
                  //     ...filter,
                  //     warehouse: undefined, // Fixed indentation
                  //   });
                  //   setPage(1);
                  // }}
                  onSelect={(val) => {
                    setFilter({
                      ...filter,
                      warehouse_id: val.id,
                      warehouse: val, // Fixed indentation
                    });
                    setPage(1);
                  }}
                /> */}
            </div>

            {/* <div>
                <label
                  htmlFor="installation_type"
                  className="text-md pt-1 font-semibold"
                >
                  Warehouse Type
                </label>
                <select
                  className="w-full p-2 bg-white rounded focus:outline-none"
                  name="warehouse_type"
                  id="warehouse_type"
                  value={filter.warehouse_type ?? ""}
                  onChange={(e) => {
                    setFilter((prevData) => ({
                      ...prevData,
                      warehouse_type: e.target.value,
                    }));
                  }}
                >
                  <option value="">select</option>
                  <option value="internal">Internal</option>
                  <option value="external">External</option>
                </select>
              </div> */}

            {/* <div>
                <label
                  htmlFor="installation_type"
                  className="text-md pt-1 font-semibold"
                >
                  Warehouse Sub-Type
                </label>
                <select
                  className="w-full p-2 bg-white rounded focus:outline-none"
                  name="warehouse_sub_type"
                  id="warehouse_sub_type"
                  value={filter.warehouse_sub_type ?? ""}
                  onChange={(e) => {
                    setFilter((prevData) => ({
                      ...prevData,
                      warehouse_sub_type: e.target.value,
                    }));
                  }}
                >
                  {filter.warehouse_type === "internal" && (
                    <>
                      <option value="">select</option>
                      <option value="kazam warehouse">
                        Internal Kazam Warehouse
                      </option>
                      <option value="city engineer warehouse">
                        City Engineer Warehouse
                      </option>
                    </>
                  )}
                  {filter.warehouse_type === "external" && (
                    <>
                      <option value="">select</option>
                      <option value="manpower">Manpower</option>
                      <option value="vendor">Vendor</option>
                    </>
                  )}
                </select>
              </div> */}
            <div className="flex flex-col gap-1">
              <label htmlFor="as_of_date" className="">
                As of Date
              </label>
              <input
                type="date"
                id="as_of_date"
                className="p-1 rounded focus:outline-none "
                value={
                  filter.as_of_date
                    ? moment(filter.as_of_date).format("YYYY-MM-DD")
                    : ""
                }
                onChange={(e) => {
                  const selectedDate = UTCToLocalDate(e.target.value);
                  setFilter((prevFilter) => ({
                    ...prevFilter,
                    as_of_date: selectedDate,
                  }));
                }}
              />
            </div>
          </div>
        </section>
        <div className="my-2 border overflow-auto" style={{ height: "80%" }}>
          <table className="w-full     text-xs divide-y divide-gray-200 relative ">
            <thead className="  text-xs   py-2 sticky -top-1 bg-white z-10">
              <tr className="border ">
                {/* <th className="px-2 py-2">Actions</th> */}
                <th className="px-6 py-3 whitespace-nowrap text-left">
                  Item Code
                </th>
                <th className="px-6 py-3 whitespace-nowrap text-left">
                  Item Name
                </th>
                {/* <th className="px-6 py-3 w-60 text-left">Item Description</th> */}
                {/* <th className="px-6 py-3 whitespace-nowrap text-left">
                  Item Category
                </th> */}
                <th className="px-6 py-3 whitespace-nowrap text-left">
                  Item Category
                </th>
                {/* <th className="px-6 py-3 whitespace-nowrap text-left">
                  Manufacturer
                </th> */}
                {/* <th className="px-6 py-3 whitespace-nowrap text-left">MPN</th> */}
                <th className="px-6 py-3 whitespace-nowrap text-left">
                  Warehouse
                </th>
                <th className="px-6 py-3 whitespace-nowrap text-right">
                  Stock Available
                </th>
                <th className="px-6 py-3 whitespace-nowrap text-right">
                  Pending Outwards
                </th>
                <th className="px-6 py-3 whitespace-nowrap text-right">
                  Physical Stock
                </th>
              </tr>
            </thead>
            <tbody className="">
              {data?.length > 0 ? (
                data.map((data, i) => {
                  return (
                    <tr
                      id={`stock_row_${data.id ?? i}`}
                      className="border text-center "
                      key={i}
                    >
                      <td className="px-6 py-3 whitespace-nowrap text-left">
                        {data?.item_code}
                      </td>

                      <td className="px-6 py-3 text-left w-60 whitespace-normal break-words">
                        {data?.item?.name ? (
                          <>
                            <span className="font-bold">{data.item.name}</span>
                            {data?.item?.description && (
                              <span> - {data.item.description}</span>
                            )}
                          </>
                        ) : (
                          (data?.item?.name ?? " ")
                        )}
                      </td>
                      {/* <td className="px-6 py-3 text-left w-60 whitespace-normal break-words">
                        {data?.item?.description}
                      </td> */}
                      <td className="px-6 py-3 whitespace-nowrap text-left">
                        {data?.item?.subcategory !== "Finished Goods"
                          ? "Raw Materials"
                          : data?.item?.subcategory}
                      </td>
                      {/* <td className="px-6 py-3 whitespace-nowrap text-left">
                        {data?.item?.manufacturer ?? " "}
                      </td> */}
                      {/* <td className="px-6 py-3 whitespace-nowrap text-left">
                        {data?.item?.mpn ?? " "}
                      </td> */}
                      <td className="px-6 py-3 whitespace-nowrap text-left">
                        {data?.warehouse?.name ?? " "}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-right">
                        <span className="inline-flex items-center gap-1 justify-end">
                          {formatAmountWithCommas(
                            Math.max(data?.closing_stock_qty ?? 0, 0),
                          ) ?? " "}
                          {data?.is_over_allocated && (
                            <Icon
                              path={mdiAlertCircleOutline}
                              size={0.7}
                              color="#dc2626"
                              title={`Over-allocated: raw balance is ${data?.closing_stock_qty}`}
                            />
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-right">
                        {formatAmountWithCommas(data?.pending_outwards) ?? " "}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-right">
                        {formatAmountWithCommas(data?.physical_stock) ?? " "}
                      </td>
                      {/* <td className="px-6 py-3 whitespace-nowrap text-left">
                                {moment(data.created_at).format("DD-MM-YYYY")}
                              </td> */}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={13}>
                    <div className="h-56 text-center p-5 my-20">
                      <h5 className="text-gray-400 text-2xl">No Data found!</h5>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-3">
          <Pagination
            itemCount={data.length}
            page={filter.page}
            total={total}
            count={filter.count}
            onChange={(page) => setFilter((o) => ({ ...o, page }))}
          />
        </div>
      </div>
      {loading && <LoadingWidget />}
    </>
  );
};

export default ClosingStockListScreen;
//
